import { MessageMultiple01Icon } from "@hugeicons-pro/core-solid-rounded";
import {
  BubbleChatIcon,
  Mail01Icon,
  Message01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import type { IconSvgElement } from "@hugeicons/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { formatCalendar, formatTimeRange } from "../helpers/format-calendar";
import { InboxMessageChannel, Notification, NotificationType } from "../types";

const CHANNEL_META: Record<
  InboxMessageChannel,
  { label: string; icon: IconSvgElement }
> = {
  [InboxMessageChannel.Email]: {
    label: "Email",
    icon: Mail01Icon
  },
  [InboxMessageChannel.Sms]: {
    label: "SMS",
    icon: Message01Icon
  },
  [InboxMessageChannel.Chat]: {
    label: "Chat",
    icon: BubbleChatIcon
  }
};

// The inbox view selects a conversation via the `c` query param, and SMS/chat threads open under
// their own composer tab (`tab=sms` / `tab=chat`). Email sets no tab, so it opens on the chip the
// user last picked in that conversation (Email if none).
// Keep this in sync with the backend Notification#url builder.
const CHANNEL_TAB_PARAM: Partial<Record<InboxMessageChannel, string>> = {
  [InboxMessageChannel.Sms]: "sms",
  [InboxMessageChannel.Chat]: "chat"
};

const buildInboxUrl = (
  conversationId: string,
  channel?: InboxMessageChannel
): string => {
  const base = `/inbox?c=${conversationId}`;
  const tab = channel ? CHANNEL_TAB_PARAM[channel] : undefined;
  return tab ? `${base}&tab=${tab}` : base;
};

interface InboxMessageContentProps {
  channel?: InboxMessageChannel;
  senderName: string;
  preview: string;
  propertyName?: string;
  timeLabel: string;
  unread: boolean;
}

// Shared inner layout for a single inbox message, reused by the immediate card and by each row of
// the digest so digest entries render identically to immediate notifications.
const InboxMessageContent = ({
  channel,
  senderName,
  preview,
  propertyName,
  timeLabel,
  unread
}: InboxMessageContentProps) => {
  const channelMeta = channel ? CHANNEL_META[channel] : undefined;
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0 text-sm text-content-400">
          {propertyName ? (
            <span className="truncate">{propertyName}</span>
          ) : null}
          {propertyName && channelMeta ? (
            <span
              className="size-1 shrink-0 rounded-full bg-content-300"
              aria-hidden
            />
          ) : null}
          {channelMeta ? (
            <span className="flex items-center gap-1.5 shrink-0">
              <HugeiconsIcon
                icon={channelMeta.icon}
                size={16}
                className="text-content-300"
                aria-hidden
              />
              {channelMeta.label}
            </span>
          ) : null}
        </div>
        <p className="text-content-300 font-medium text-sm shrink-0">
          {timeLabel}
        </p>
      </div>
      <div className="flex items-center gap-4 min-w-0">
        <span
          className={cx(
            "size-2.5 rounded-full shrink-0",
            unread ? "bg-primary-500" : "bg-alpha-40"
          )}
        />
        <Avatar name={senderName} size={44} className="font-bold" />
        <div className="min-w-0">
          <p className="text-lg font-bold text-content-1000 truncate">
            {senderName}
          </p>
          <p className="text-base text-content-400 truncate">{preview}</p>
        </div>
      </div>
    </>
  );
};

interface InboxNotificationCardProps {
  item: Notification;
}

export const InboxNotificationCard = ({ item }: InboxNotificationCardProps) => {
  const queryClient = useQueryClient();
  const { id, read_at, url, type, metadata, created_at } = item;

  const invalidateNotificationCounts = () => {
    queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
    queryClient.invalidateQueries({
      queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
    });
    queryClient.invalidateQueries({
      queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
    });
    queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
  };

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: invalidateNotificationCounts
  });

  // Digests aggregate N messages into one row; marking a single message read only clears that
  // message (unread badge -1) and leaves the rest of the digest unread until every message is read.
  const { mutate: markDigestMessageRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_DIGEST_MESSAGE_READ"],
    mutationFn: (messageIndex: number) =>
      instance.post(`/portal/v1/notifications/${id}/mark_digest_message_read`, {
        message_index: messageIndex
      }),
    onSuccess: invalidateNotificationCounts
  });

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const handleMessageClick = (messageIndex: number, messageRead: boolean) => {
    if (!read_at && !messageRead) markDigestMessageRead(messageIndex);
  };

  const isDigest = type === NotificationType.InboxMessageDigest;
  const formattedCreateAt = formatCalendar(created_at);

  const digestSummary = useMemo(() => {
    if (!isDigest) return "";
    const channelLabels = (metadata.channels ?? [])
      .map((channel) => CHANNEL_META[channel]?.label)
      .filter(Boolean);
    return channelLabels.length
      ? `New messages via ${channelLabels.join(", ")}`
      : "New messages";
  }, [isDigest, metadata.channels]);

  if (isDigest) {
    const count = metadata.count ?? 0;
    const digestMessages = metadata.messages ?? [];
    const windowLabel =
      metadata.window_start && metadata.window_end
        ? formatTimeRange(metadata.window_start, metadata.window_end)
        : "";
    return (
      <div className="bg-white">
        <Link
          to={url}
          onClick={handleClick}
          className="p-6 hover:bg-alpha-40 cursor-pointer flex w-full"
        >
          <div className="w-full flex justify-between gap-6 items-center">
            <div className="flex gap-4 items-center min-w-0">
              <span
                className={cx(
                  "size-3 rounded-full shrink-0",
                  read_at ? "bg-alpha-40" : "bg-primary-500"
                )}
              />
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-20">
                <HugeiconsIcon
                  icon={MessageMultiple01Icon}
                  size={20}
                  className="text-primary-500"
                  aria-hidden
                />
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-content-1000 truncate">
                  {count} new {count === 1 ? "message" : "messages"}
                </p>
                <p className="text-sm text-content-400 truncate">
                  {digestSummary}
                </p>
              </div>
            </div>
            <p className="text-content-300 font-medium text-sm shrink-0">
              {windowLabel}
            </p>
          </div>
        </Link>
        {digestMessages.length ? (
          <div className="border-t border-content-80 divide-y divide-content-80">
            {digestMessages.map((message, index) => {
              const messageRead = Boolean(read_at) || Boolean(message.read_at);
              const messageTo = message.conversation_id
                ? buildInboxUrl(message.conversation_id, message.channel)
                : url;
              return (
                <Link
                  key={`${message.conversation_id ?? "msg"}-${index}`}
                  to={messageTo}
                  onClick={() => handleMessageClick(index, messageRead)}
                  className="p-6 pl-16 hover:bg-alpha-40 cursor-pointer flex flex-col gap-3 w-full"
                >
                  <InboxMessageContent
                    channel={message.channel}
                    senderName={message.sender_name || "New message"}
                    preview={message.preview || "-"}
                    propertyName={message.property_name}
                    timeLabel={
                      message.occurred_at
                        ? formatCalendar(message.occurred_at)
                        : formattedCreateAt
                    }
                    unread={!messageRead}
                  />
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Link
      to={url}
      onClick={handleClick}
      className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex flex-col gap-3 w-full"
    >
      <InboxMessageContent
        channel={metadata.channel}
        senderName={metadata.sender_name || "New message"}
        preview={metadata.preview || "-"}
        propertyName={metadata.property_name}
        timeLabel={formattedCreateAt}
        unread={!read_at}
      />
    </Link>
  );
};
